import { useEffect, useRef } from "react";
import axios from "axios";

import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { API_BASE as API } from "../lib/apiBase";

/**
 * Tâche 1329 (Eric 2026-06-30) — La langue suit la PERSONNE, pas le poste.
 *
 * Avant : le choix FR|EN vivait dans le stockage local du navigateur. Un
 * anglophone qui ouvrait Knoria depuis un autre poste (ou après un nettoyage
 * de cache) retombait en français et devait retrouver le bouton à chaque
 * fois. La préférence est désormais rangée sur le compte
 * (`preferences.lang`), via l'endpoint de préférences qui existait déjà.
 *
 * Ce composant est le SEUL point de contact entre l'authentification et la
 * langue. `LanguageContext` reste ignorant du compte connecté : il est monté
 * au-dessus de `AuthProvider` (l'écran de connexion doit pouvoir se traduire
 * avant qu'un compte existe), il ne peut donc pas lire `useAuth`. Monté
 * ici, à l'intérieur des deux, il lit l'un et écrit dans l'autre.
 *
 * Règles arbitrées avec Eric :
 *   - à la connexion, la langue DU COMPTE gagne sur celle du navigateur —
 *     À LA CONNEXION SEULEMENT, pas à chaque rechargement de page (1389) ;
 *   - un clic sur FR|EN enregistre sur le compte ET localement ;
 *   - un visiteur non connecté garde son choix local (aucun appel réseau).
 */
export function SyncLangueCompte() {
  const { user } = useAuth();
  const { lang, appliquerLangueDuCompte } = useLang();
  // Dernière valeur connue côté serveur. Sans ce repère, le PATCH ci-dessous
  // repartirait à chaque nouveau rendu portant la même langue.
  const langueEnregistree = useRef(null);
  const compteApplique = useRef(null);
  // La langue du compte a-t-elle fini d'être appliquée ? Tant que non, la
  // valeur de `lang` est encore celle du NAVIGATEUR.
  const pret = useRef(false);

  const langueDuCompte = user?.preferences?.lang;
  const idCompte = user?.id || user?.email || null;

  // 1) À l'arrivée du compte : sa langue s'impose (une seule fois par compte,
  //    sinon un clic sur FR|EN serait aussitôt annulé par cet effet).
  //
  //    Tâche 1389 (Eric 2026-06-30) — « une seule fois » se comptait PAR
  //    MONTAGE, donc à nouveau après chaque rechargement complet de la page.
  //    Conséquence mesurée : on clique EN, la préférence part vers le compte,
  //    on navigue avant que l'appel ait abouti — et le rechargement suivant
  //    réimpose la langue du compte, encore « fr ». Le choix est perdu sans
  //    rien dire, et l'application paraît ne pas être traduite du tout.
  //    Le repère vit maintenant dans la session du navigateur : la langue du
  //    compte s'impose UNE FOIS À LA CONNEXION (la règle arbitrée avec Eric),
  //    pas à chaque F5.
  const cleSession = idCompte ? `knoria-langue-appliquee:${idCompte}` : null;
  useEffect(() => {
    if (!idCompte || compteApplique.current === idCompte) return;
    let dejaApplique = false;
    try {
      dejaApplique = sessionStorage.getItem(cleSession) === "1";
    } catch { /* session indisponible : on retombe sur le repère en mémoire */ }
    compteApplique.current = idCompte;
    if (dejaApplique) {
      // Déjà fait pour cette session : le choix courant de la personne est le
      // bon, et il peut être enregistré tel quel.
      langueEnregistree.current = langueDuCompte || null;
      pret.current = true;
      return;
    }
    try {
      sessionStorage.setItem(cleSession, "1");
    } catch { /* sans stockage de session, on garde le comportement d'avant */ }
    langueEnregistree.current = langueDuCompte || null;
    // Aucune langue sur le compte : il n'y a rien à attendre, le choix local
    // est le bon et pourra être enregistré tel quel.
    pret.current = !langueDuCompte;
    if (langueDuCompte) appliquerLangueDuCompte(langueDuCompte);
  }, [idCompte, cleSession, langueDuCompte, appliquerLangueDuCompte]);

  // 2) Après un clic sur FR|EN : on range le choix sur le compte.
  useEffect(() => {
    if (!idCompte || compteApplique.current !== idCompte) return;
    if (!pret.current) {
      // Les deux effets tournent dans le MÊME cycle de rendu : ici, `lang`
      // vaut encore la langue du navigateur, l'effet 1 venant seulement de
      // demander celle du compte. Enregistrer maintenant écraserait la
      // préférence du compte par celle du poste — exactement l'inverse de la
      // règle « le compte gagne ». On attend que `lang` l'ait rejointe.
      if (lang === langueEnregistree.current) pret.current = true;
      return;
    }
    if (langueEnregistree.current === lang) return;
    langueEnregistree.current = lang;
    axios
      .patch(`${API}/api/auth/me/preferences`, { lang }, { withCredentials: true })
      .catch(() => {
        // Échec réseau : la langue reste bonne pour la session courante et
        // dans le stockage local. On laisse le prochain clic retenter plutôt
        // que d'alerter — ce n'est pas une action que l'utilisateur a
        // demandée explicitement.
        langueEnregistree.current = null;
      });
  }, [lang, idCompte]);

  return null;
}

export default SyncLangueCompte;
